import Decimal from 'decimal.js';

/**
 * P21 + P12 PR5 – Quy tắc tài xế mới (Phương án Nhân sự v2, anh Tiến duyệt 09/10/2026; docs/phases/P21.md §9.8).
 * Ca tài xế = nhận xe → trả xe, ghi vào ngày nhận xe (trả sau nửa đêm vẫn tính ngày nhận, tối đa 12:00 hôm sau).
 * Mọi mốc giờ / đơn giá / ngưỡng ở đây là THAM SỐ (bảng driver_rule_params, phiên bản theo ngày hiệu lực); số dưới là mặc định.
 * Hàm thuần, giờ VN, tiền VND làm tròn ROUND_HALF_UP.
 */

export interface DriverRuleParams {
  // 4.4 Giao sau 17h
  /** Phiếu thuộc lệnh giao sau 17h phải giao xong từ giờ này mới tính tăng ca */
  after17MinDeliveredAt: string;
  /** Tăng ca sau 17h làm tròn xuống theo khối phút */
  otRoundMinutes: number;
  /** Tốc độ ước tính khi không có Google Maps (km/h) */
  returnSpeedKmh: number;
  /** Hệ số nhân thời gian chạy về bãi */
  returnFactor: number;
  /** Cộng thêm sau thời gian chạy về (phút) */
  returnBufferMinutes: number;
  /** GPS xe / điện thoại lúc bấm Giao xong phải cách nhau không quá (m) */
  customerGpsRadiusM: number;
  // 4.2 Về khuya
  /** Đơn giá mỗi giờ sau 0h (trả xe 00:00–04:00 hôm sau) */
  afterMidnightRate: number;
  /** Tính tiền giờ sau 0h tới mốc này */
  afterMidnightUntil: string;
  /** Ca kéo dài tối đa tới giờ này hôm sau (vẫn tính ngày nhận xe) */
  shiftMaxNextDay: string;
  // DEC-761 (anh Tiến 02/11/2026): hỗ trợ đêm theo mức giờ xe về bãi (giờ về cắt ở giờ về tối đa như tăng ca sau 17h).
  // Đặt 0 cả 3 mức đơn giá tài = tắt, dùng cách cũ (hỗ trợ đêm 1 mức từ nightStart + tiền giờ sau 0h).
  // Có mức > 0: thay cả hỗ trợ đêm cũ lẫn tiền giờ sau 0h; lấy mức cao nhất mà giờ về đạt tới (không cộng dồn).
  /**
   * DEC-762 (anh Tiến 02/11/2026): có lệnh giao gấp (lệnh giao sau 17h, nhận 16:30–17:00) thì tăng ca tính từ giờ này tới khi trả xe
   * (giờ về cắt ở giờ về tối đa). Lấy mốc muộn hơn giữa giờ này và "Tăng ca sau 17h từ" của chính sách. Khi bật hỗ trợ đêm theo mức,
   * tăng ca chạy tới mốc mức 1 (22:00), sau đó tính hỗ trợ đêm theo mức.
   */
  after17OtFrom: string;
  /** Mức 1 từ (vd 22:00) */
  nightTier1From: string;
  nightTier1Rate: number;
  nightTier1AssistantRate: number;
  /** Mức 2 từ (giờ trước 12:00 hiểu là hôm sau, vd 00:00) */
  nightTier2From: string;
  nightTier2Rate: number;
  nightTier2AssistantRate: number;
  /** Mức 3 từ (vd 02:00 hôm sau) */
  nightTier3From: string;
  nightTier3Rate: number;
  nightTier3AssistantRate: number;
  /**
   * DEC-930 (anh Tiến 04/11/2026): "Khi có đơn giao sớm tài mới được tính Tăng Ca. Xe xuất phát tại bãi → tính giờ tăng ca, như để lưu ý,
   * chờ xác nhận". 1 = tăng ca sáng KHÔNG tự tính theo giờ chấm vào; chỉ tính khi có "Đi sớm" đã duyệt (hệ thống tự đề nghị
   * cho ngày xe có phiếu giao ghi giờ yêu cầu giao sớm, giờ tính từ lúc GPS xe rời bãi). 0 = cách cũ.
   */
  earlyOrderOnly: number;
  // 4.6 Chống khai khống qua đêm
  factoryLateMinutes: number;
  waitFactor: number;
  waitHistoryDays: number;
  slipEarlyMinutes: number;
  overnightLoadedBefore: string;
  overnightMaxReturnHours: number;
  nightGpsStart: string;
  nightGpsEnd: string;
  nightGpsMaxMoveKm: number;
  // 4.7 Chuyến tỉnh
  provincePerDiem: number;
  provinceLodging: number;
  // 4.8 Quên trả xe
  returnIdleMinutes: number;
  returnSecondReminder: string;
  autoReturnAt: string;
  autoReturnNoGps: string;
  forgotFreeTimes: number;
  forgotPenalty: number;
  // 4.10 Cảnh báo
  otWarnHours: number;
  // 4.9 Km
  kmMismatchMinKm: number;
  kmMismatchPct: number;
  // Màn tài xế theo ngày (anh Tiến 24/10/2026)
  /** Từ giờ này tab Giao hàng gắn thêm đơn ngày mai */
  driverNextDayAt: string;
  /** Giờ gửi tài xế tin "Đơn hàng hôm nay gồm: …" */
  morningBriefAt: string;
  /** Lời nhắc cuối tin đầu giờ */
  morningBriefText: string;
  /** Khung hiện lời cảm ơn chạy xuyên trưa (tự tắt) */
  lunchThanksFrom: string;
  lunchThanksUntil: string;
  lunchThanksText: string;
}

export const DEFAULT_DRIVER_RULES: DriverRuleParams = {
  after17MinDeliveredAt: '16:30',
  otRoundMinutes: 15,
  returnSpeedKmh: 35,
  returnFactor: 1.3,
  returnBufferMinutes: 30,
  customerGpsRadiusM: 1000,
  afterMidnightRate: 60000,
  afterMidnightUntil: '04:00',
  shiftMaxNextDay: '12:00',
  after17OtFrom: '17:00',
  // Mặc định tắt (0đ) để không đổi lương các tháng cũ; migration 0285 bật từ 01/11/2026 với mức anh Tiến đưa
  nightTier1From: '22:00',
  nightTier1Rate: 0,
  nightTier1AssistantRate: 0,
  nightTier2From: '00:00',
  nightTier2Rate: 0,
  nightTier2AssistantRate: 0,
  nightTier3From: '02:00',
  nightTier3Rate: 0,
  nightTier3AssistantRate: 0,
  // Mặc định tắt để không đổi lương các tháng cũ; migration 0308 bật từ 05/11/2026
  earlyOrderOnly: 0,
  factoryLateMinutes: 30,
  waitFactor: 1.5,
  waitHistoryDays: 90,
  slipEarlyMinutes: 30,
  overnightLoadedBefore: '20:00',
  overnightMaxReturnHours: 4,
  nightGpsStart: '22:00',
  nightGpsEnd: '04:00',
  nightGpsMaxMoveKm: 2,
  provincePerDiem: 150000,
  provinceLodging: 200000,
  returnIdleMinutes: 30,
  returnSecondReminder: '21:00',
  autoReturnAt: '23:59',
  autoReturnNoGps: '17:00',
  forgotFreeTimes: 2,
  forgotPenalty: 50000,
  otWarnHours: 40,
  kmMismatchMinKm: 50,
  kmMismatchPct: 5,
  driverNextDayAt: '16:00',
  morningBriefAt: '07:00',
  morningBriefText: 'Đề nghị anh em tranh thủ.',
  lunchThanksFrom: '13:00',
  lunchThanksUntil: '14:00',
  lunchThanksText: 'Cảm ơn bác tài đã chạy xuyên trưa!',
};

export type DriverRuleKind = 'time' | 'money' | 'int' | 'number' | 'text';
export interface DriverRuleField {
  key: keyof DriverRuleParams;
  label: string;
  group: string;
  kind: DriverRuleKind;
  min?: number;
  /** Số: giá trị lớn nhất; chữ (text): số ký tự tối đa */
  max?: number;
  unit?: string;
}

/** Mô tả từng tham số: nhãn trên màn Chấm công → Tài xế → Quy tắc, kiểu, giới hạn (API kiểm theo bảng này). */
export const DRIVER_RULE_FIELDS: DriverRuleField[] = [
  { key: 'after17MinDeliveredAt', label: 'Giao xong từ (mới tính tăng ca sau 17h)', group: 'Giao sau 17h', kind: 'time' },
  { key: 'otRoundMinutes', label: 'Làm tròn tăng ca (phút)', group: 'Giao sau 17h', kind: 'int', min: 1, max: 60, unit: 'phút' },
  { key: 'returnSpeedKmh', label: 'Tốc độ ước tính khi không có bản đồ', group: 'Giao sau 17h', kind: 'number', min: 5, max: 120, unit: 'km/h' },
  { key: 'returnFactor', label: 'Hệ số thời gian chạy về bãi', group: 'Giao sau 17h', kind: 'number', min: 1, max: 5, unit: '×' },
  { key: 'returnBufferMinutes', label: 'Cộng thêm sau thời gian chạy về', group: 'Giao sau 17h', kind: 'int', min: 0, max: 240, unit: 'phút' },
  { key: 'customerGpsRadiusM', label: 'GPS xe cách chỗ bấm Giao xong tối đa', group: 'Giao sau 17h', kind: 'int', min: 50, max: 20000, unit: 'm' },
  { key: 'afterMidnightRate', label: 'Mỗi giờ sau 0h', group: 'Về khuya', kind: 'money', unit: 'đ/giờ' },
  { key: 'afterMidnightUntil', label: 'Tính giờ sau 0h tới', group: 'Về khuya', kind: 'time' },
  { key: 'shiftMaxNextDay', label: 'Ca tính vào ngày nhận xe tới tối đa (hôm sau)', group: 'Về khuya', kind: 'time' },
  { key: 'after17OtFrom', label: 'Lệnh giao gấp: tăng ca tính từ', group: 'Giao sau 17h', kind: 'time' },
  { key: 'nightTier1From', label: 'Mức 1: xe về bãi từ', group: 'Hỗ trợ đêm theo mức', kind: 'time' },
  { key: 'nightTier1Rate', label: 'Mức 1: tài xế', group: 'Hỗ trợ đêm theo mức', kind: 'money', unit: 'đ/đêm' },
  { key: 'nightTier1AssistantRate', label: 'Mức 1: phụ xe', group: 'Hỗ trợ đêm theo mức', kind: 'money', unit: 'đ/đêm' },
  { key: 'nightTier2From', label: 'Mức 2: xe về bãi từ (trước 12:00 = hôm sau)', group: 'Hỗ trợ đêm theo mức', kind: 'time' },
  { key: 'nightTier2Rate', label: 'Mức 2: tài xế', group: 'Hỗ trợ đêm theo mức', kind: 'money', unit: 'đ/đêm' },
  { key: 'nightTier2AssistantRate', label: 'Mức 2: phụ xe', group: 'Hỗ trợ đêm theo mức', kind: 'money', unit: 'đ/đêm' },
  { key: 'nightTier3From', label: 'Mức 3: xe về bãi từ (hôm sau)', group: 'Hỗ trợ đêm theo mức', kind: 'time' },
  { key: 'nightTier3Rate', label: 'Mức 3: tài xế', group: 'Hỗ trợ đêm theo mức', kind: 'money', unit: 'đ/đêm' },
  { key: 'nightTier3AssistantRate', label: 'Mức 3: phụ xe', group: 'Hỗ trợ đêm theo mức', kind: 'money', unit: 'đ/đêm' },
  { key: 'earlyOrderOnly', label: 'Tăng ca sáng chỉ khi có đơn giao sớm, chờ xác nhận (1 = bật, 0 = tắt)', group: 'Tăng ca sáng', kind: 'int', min: 0, max: 1 },
  { key: 'factoryLateMinutes', label: 'Tới nhà máy trễ hơn giờ trên lệnh', group: 'Qua đêm, bốc chậm', kind: 'int', min: 0, max: 600, unit: 'phút' },
  { key: 'waitFactor', label: 'Chờ bốc vượt trung bình nhà máy', group: 'Qua đêm, bốc chậm', kind: 'number', min: 1, max: 10, unit: 'lần' },
  { key: 'waitHistoryDays', label: 'Trung bình chờ bốc tính trong', group: 'Qua đêm, bốc chậm', kind: 'int', min: 7, max: 365, unit: 'ngày' },
  { key: 'slipEarlyMinutes', label: 'Giờ trên phiếu xuất sớm hơn giờ bấm quá', group: 'Qua đêm, bốc chậm', kind: 'int', min: 0, max: 600, unit: 'phút' },
  { key: 'overnightLoadedBefore', label: 'Lấy xong trước giờ này mà ở lại = cờ đỏ', group: 'Qua đêm, bốc chậm', kind: 'time' },
  { key: 'overnightMaxReturnHours', label: '… và đường về dưới', group: 'Qua đêm, bốc chậm', kind: 'number', min: 0, max: 48, unit: 'giờ' },
  { key: 'nightGpsStart', label: 'Khung kiểm GPS xe đêm từ', group: 'Qua đêm, bốc chậm', kind: 'time' },
  { key: 'nightGpsEnd', label: 'Khung kiểm GPS xe đêm tới', group: 'Qua đêm, bốc chậm', kind: 'time' },
  { key: 'nightGpsMaxMoveKm', label: 'Xe rời chỗ ở lại quá', group: 'Qua đêm, bốc chậm', kind: 'number', min: 0, max: 100, unit: 'km' },
  { key: 'provincePerDiem', label: 'Công tác phí chuyến tỉnh', group: 'Chuyến tỉnh', kind: 'money', unit: 'đ/ngày' },
  { key: 'provinceLodging', label: 'Lưu trú chuyến tỉnh', group: 'Chuyến tỉnh', kind: 'money', unit: 'đ/đêm' },
  { key: 'returnIdleMinutes', label: 'Xe về bãi đứng yên mà chưa trả → nhắc sau', group: 'Quên trả xe', kind: 'int', min: 5, max: 600, unit: 'phút' },
  { key: 'returnSecondReminder', label: 'Nhắc lần 2 + báo quản lý lúc', group: 'Quên trả xe', kind: 'time' },
  { key: 'autoReturnAt', label: 'Tự trả xe lúc', group: 'Quên trả xe', kind: 'time' },
  { key: 'autoReturnNoGps', label: 'Không có GPS xe thì chốt giờ trả', group: 'Quên trả xe', kind: 'time' },
  { key: 'forgotFreeTimes', label: 'Số lần quên không trừ tiền / tháng', group: 'Quên trả xe', kind: 'int', min: 0, max: 31, unit: 'lần' },
  { key: 'forgotPenalty', label: 'Trừ mỗi lần quên (từ lần kế tiếp)', group: 'Quên trả xe', kind: 'money', unit: 'đ/lần' },
  { key: 'otWarnHours', label: 'Cảnh báo tăng ca vượt', group: 'Giới hạn', kind: 'number', min: 0, max: 300, unit: 'giờ/tháng' },
  { key: 'kmMismatchMinKm', label: 'Cờ lệch km GPS – ODO tối thiểu', group: 'Km', kind: 'number', min: 0, max: 1000, unit: 'km' },
  { key: 'kmMismatchPct', label: 'Cờ lệch km GPS – ODO', group: 'Km', kind: 'number', min: 0, max: 100, unit: '%' },
  { key: 'driverNextDayAt', label: 'Gắn đơn ngày mai vào tab Giao hàng từ', group: 'Màn tài xế', kind: 'time' },
  { key: 'morningBriefAt', label: 'Gửi tin "Đơn hàng hôm nay gồm…" lúc', group: 'Màn tài xế', kind: 'time' },
  { key: 'morningBriefText', label: 'Lời nhắc cuối tin đầu giờ', group: 'Màn tài xế', kind: 'text', max: 200 },
  { key: 'lunchThanksFrom', label: 'Hiện lời cảm ơn chạy xuyên trưa từ', group: 'Màn tài xế', kind: 'time' },
  { key: 'lunchThanksUntil', label: '… tự tắt lúc', group: 'Màn tài xế', kind: 'time' },
  { key: 'lunchThanksText', label: 'Lời cảm ơn chạy xuyên trưa', group: 'Màn tài xế', kind: 'text', max: 200 },
];

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Kiểm + chuẩn hóa tham số (API và form). Trả danh sách lỗi theo khóa. */
export function validateDriverRules(input: Record<string, unknown>): { value: DriverRuleParams | null; errors: { path: string; message: string }[] } {
  const errors: { path: string; message: string }[] = [];
  const out: Record<string, unknown> = {};
  for (const f of DRIVER_RULE_FIELDS) {
    const v = input[f.key];
    if (f.kind === 'time') {
      if (typeof v !== 'string' || !HHMM_RE.test(v)) errors.push({ path: f.key, message: `${f.label}: nhập giờ HH:MM` });
      else out[f.key] = v;
      continue;
    }
    if (f.kind === 'text') {
      const t = typeof v === 'string' ? v.trim() : '';
      if (!t || t.length > (f.max ?? 200)) errors.push({ path: f.key, message: `${f.label}: nhập 1–${f.max ?? 200} ký tự` });
      else out[f.key] = t;
      continue;
    }
    const n = typeof v === 'number' ? v : Number.NaN;
    const min = f.min ?? 0;
    const max = f.max ?? 100_000_000;
    if (!Number.isFinite(n) || n < min || n > max) errors.push({ path: f.key, message: `${f.label}: từ ${min} tới ${max}` });
    else if ((f.kind === 'int' || f.kind === 'money') && !Number.isInteger(n)) errors.push({ path: f.key, message: `${f.label}: nhập số nguyên` });
    else out[f.key] = f.kind === 'number' ? Math.round(n * 100) / 100 : n;
  }
  // Anh Tiến 05/11/2026 (kiểm tiền tài xế T10): mốc hỗ trợ đêm theo mức phải tăng dần (22:00 → 00:00 → 02:00); giờ trước 12:00 là
  // rạng sáng hôm sau. Gõ "12:00" (ý là 12 giờ đêm) thành 12 giờ TRƯA → mọi ca về sau 12:00 đều ăn mức 2.
  if (!errors.length && nightTiersOn(out as unknown as DriverRuleParams)) {
    const [t1, t2, t3] = (['nightTier1From', 'nightTier2From', 'nightTier3From'] as const).map((k) => tierMinute(out[k] as string));
    if (t1! < 18 * 60) errors.push({ path: 'nightTier1From', message: 'Mức 1 hỗ trợ đêm: từ 18:00 trở đi (VD 22:00)' });
    if (!(t2! > t1!)) errors.push({ path: 'nightTier2From', message: 'Mức 2 phải sau mức 1 (VD 00:00 = 12 giờ đêm; 12:00 là 12 giờ trưa)' });
    if (!(t3! > t2!)) errors.push({ path: 'nightTier3From', message: 'Mức 3 phải sau mức 2 (VD 02:00 rạng sáng)' });
  }
  return { value: errors.length ? null : (out as unknown as DriverRuleParams), errors };
}

/** Tham số đã lưu có thể thiếu khóa mới thêm sau → bù mặc định. */
export const withRuleDefaults = (p: Partial<DriverRuleParams> | null | undefined): DriverRuleParams => ({ ...DEFAULT_DRIVER_RULES, ...(p ?? {}) });

// ---------------------------------------------------------------------------
// Tiện ích giờ
// ---------------------------------------------------------------------------
export const hhmmToMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
export const minToHHMM = (m: number) => {
  const x = ((Math.round(m) % 1440) + 1440) % 1440;
  return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
};
/** Làm tròn XUỐNG theo khối phút (tăng ca tròn 15 phút). */
export const floorTo = (minutes: number, block: number) => (minutes <= 0 ? 0 : Math.floor(minutes / block) * block);

// ---------------------------------------------------------------------------
// 4.4 Giờ về tối đa = giờ giao xong phiếu cuối + thời gian chạy về × hệ số + đệm
// ---------------------------------------------------------------------------
/** Thời gian chạy về khi không có Google Maps: km ÷ tốc độ (phút, làm tròn lên). */
export const travelMinutesByKm = (km: number, p: DriverRuleParams = DEFAULT_DRIVER_RULES) => Math.ceil((Math.max(0, km) / p.returnSpeedKmh) * 60);

/** Phút (tính từ 0h ngày nhận xe) muộn nhất được tính là giờ về. */
export function maxReturnMinute(lastDeliveredMinute: number, travelMinutes: number, p: DriverRuleParams = DEFAULT_DRIVER_RULES): number {
  return lastDeliveredMinute + Math.ceil(travelMinutes * p.returnFactor) + p.returnBufferMinutes;
}

// ---------------------------------------------------------------------------
// 4.8 Quên trả xe: từ lần (forgotFreeTimes + 1) trong tháng trừ forgotPenalty / lần
// ---------------------------------------------------------------------------
export function forgotReturnPenalty(nthInMonth: number, p: DriverRuleParams = DEFAULT_DRIVER_RULES): number {
  return nthInMonth > p.forgotFreeTimes ? p.forgotPenalty : 0;
}

/** Giờ chốt khi tự trả xe: giờ GPS xe vào bãi; không có GPS → autoReturnNoGps của ngày nhận xe. */
export function autoReturnTime(receiveDate: string, gpsArrivedAt: string | null, p: DriverRuleParams = DEFAULT_DRIVER_RULES): string {
  if (gpsArrivedAt) return gpsArrivedAt;
  const [h, m] = p.autoReturnNoGps.split(':');
  return new Date(Date.parse(`${receiveDate}T${h}:${m}:00+07:00`)).toISOString();
}

// ---------------------------------------------------------------------------
// 4.7 Chuyến tỉnh nhiều ngày
// ---------------------------------------------------------------------------
export interface ProvinceTripPay {
  days: number;
  nights: number;
  perDiem: number;
  lodging: number;
  amount: number;
  /** Ngày Chủ nhật trên chuyến (vào khoản "Làm Chủ nhật") */
  sundays: string[];
  overPlanned: boolean;
  dates: string[];
}

const addDaysIso = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};

/** Mỗi ngày trên chuyến (ngày đi → ngày về, tính cả hai đầu) = 1 công + công tác phí; mỗi đêm ở lại = lưu trú. */
export function provinceTripPay(
  t: { startDate: string; endDate: string; nights: number; plannedDays: number },
  p: DriverRuleParams = DEFAULT_DRIVER_RULES,
): ProvinceTripPay {
  const dates: string[] = [];
  for (let d = t.startDate; d <= t.endDate && dates.length < 60; d = addDaysIso(d, 1)) dates.push(d);
  const days = dates.length;
  const nights = Math.max(0, Math.min(t.nights, Math.max(0, days - 1)));
  const perDiem = new Decimal(days).mul(p.provincePerDiem).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
  const lodging = new Decimal(nights).mul(p.provinceLodging).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
  return {
    days,
    nights,
    perDiem,
    lodging,
    amount: perDiem + lodging,
    sundays: dates.filter((d) => new Date(`${d}T00:00:00Z`).getUTCDay() === 0),
    overPlanned: days > t.plannedDays,
    dates,
  };
}

// ---------------------------------------------------------------------------
// 4.9 Km: km khoán = min(GPS, ODO); cờ khi lệch quá max(kmMismatchMinKm, kmMismatchPct %)
// ---------------------------------------------------------------------------
export function kmContract(gpsKm: number | null, odoKm: number | null, p: DriverRuleParams = DEFAULT_DRIVER_RULES) {
  const gps = gpsKm === null ? null : Math.round(gpsKm * 10) / 10;
  const odo = odoKm === null ? null : Math.round(odoKm * 10) / 10;
  const contracted = gps === null ? odo : odo === null ? gps : Math.min(gps, odo);
  let mismatch = false;
  if (gps !== null && odo !== null) {
    const limit = Math.max(p.kmMismatchMinKm, (Math.max(gps, odo) * p.kmMismatchPct) / 100);
    mismatch = Math.abs(gps - odo) > limit;
  }
  return { gps, odo, contracted, mismatch };
}

// ---------------------------------------------------------------------------
// 4.6 Cờ đỏ chuyến lấy hàng / qua đêm
// ---------------------------------------------------------------------------
export const TRIP_FLAG_LABELS = {
  late_arrival: 'Tới nhà máy trễ hơn giờ trên lệnh, không lý do',
  long_wait: 'Chờ bốc vượt trung bình của nhà máy',
  slip_early: 'Giờ trên phiếu xuất sớm hơn giờ bấm Lấy hàng xong',
  needless_overnight: 'Lấy xong sớm, đường về ngắn mà vẫn ở lại',
  moved_at_night: 'Xe rời chỗ ở lại trong đêm',
  no_slip_photo: 'Thiếu ảnh phiếu xuất / phiếu cân',
} as const;
export type TripFlag = keyof typeof TRIP_FLAG_LABELS;

export interface TripFlagInput {
  appointmentAt: string | null;
  arrivedAt: string | null;
  lateReason: string | null;
  loadedAt: string | null;
  slipTime: string | null;
  slipPhotoId: number | null;
  overnight: boolean;
  /** Trung bình chờ bốc (phút) 90 ngày của nhà máy; null = chưa đủ dữ liệu */
  avgWaitMinutes: number | null;
  /** Thời gian chạy về bãi (phút); null = không biết */
  returnTravelMinutes: number | null;
  /** Xe rời xa nhất khỏi chỗ ở lại trong khung đêm (km); null = không có GPS xe */
  nightMaxMoveKm: number | null;
}

const vnMin = (iso: string) => {
  const d = new Date(Date.parse(iso) + 7 * 3_600_000);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
};

export function tripFlags(t: TripFlagInput, p: DriverRuleParams = DEFAULT_DRIVER_RULES): TripFlag[] {
  const out: TripFlag[] = [];
  if (t.appointmentAt && t.arrivedAt && !t.lateReason?.trim() && Date.parse(t.arrivedAt) - Date.parse(t.appointmentAt) > p.factoryLateMinutes * 60_000) out.push('late_arrival');
  if (t.arrivedAt && t.loadedAt && t.avgWaitMinutes !== null && t.avgWaitMinutes > 0) {
    const wait = (Date.parse(effectiveLoadedAt(t)) - Date.parse(t.arrivedAt)) / 60_000;
    if (wait > t.avgWaitMinutes * p.waitFactor) out.push('long_wait');
  }
  if (t.loadedAt && t.slipTime && Date.parse(t.loadedAt) - Date.parse(t.slipTime) > p.slipEarlyMinutes * 60_000) out.push('slip_early');
  if (t.loadedAt && !t.slipPhotoId) out.push('no_slip_photo');
  if (t.overnight && t.loadedAt) {
    const L = vnMin(effectiveLoadedAt(t));
    const before = L >= 4 * 60 && L < hhmmToMin(p.overnightLoadedBefore);
    if (before && t.returnTravelMinutes !== null && t.returnTravelMinutes < p.overnightMaxReturnHours * 60) out.push('needless_overnight');
  }
  if (t.overnight && t.nightMaxMoveKm !== null && t.nightMaxMoveKm > p.nightGpsMaxMoveKm) out.push('moved_at_night');
  return out;
}

/** Tiền lấy hàng tính theo giờ trên phiếu khi giờ đó sớm hơn giờ bấm (chống bấm muộn để tăng tiền). */
export function effectiveLoadedAt(t: { loadedAt: string | null; slipTime: string | null }): string {
  if (!t.loadedAt) return t.slipTime ?? '';
  if (t.slipTime && Date.parse(t.slipTime) < Date.parse(t.loadedAt)) return t.slipTime;
  return t.loadedAt;
}

/** Qua đêm không tính khi xe rời chỗ ở lại quá ngưỡng trong khung đêm. */
export const overnightVoided = (flags: readonly TripFlag[]) => flags.includes('moved_at_night');

// ---------------------------------------------------------------------------
// 4.1 Checklist nhận xe
// ---------------------------------------------------------------------------
export interface ChecklistResultInput {
  itemId: number;
  ok: boolean;
  photoId?: number | null;
  note?: string | null;
}
export interface ChecklistItemDef {
  id: number;
  label: string;
  severe: boolean;
}
export const CHECKLIST_CORNERS = ['front', 'back', 'left', 'right'] as const;
export type ChecklistCorner = (typeof CHECKLIST_CORNERS)[number];
export const CHECKLIST_CORNER_LABELS: Record<ChecklistCorner, string> = {
  front: 'Ảnh đầu xe',
  back: 'Ảnh đuôi xe',
  left: 'Ảnh hông trái',
  right: 'Ảnh hông phải',
};
export const CHECKLIST_STATUS_LABELS = {
  passed: 'Đạt',
  minor: 'Có lỗi nhẹ (cho chạy)',
  locked: 'Khóa xe (lỗi nghiêm trọng)',
  unlocked: 'Đã mở khóa',
} as const;
export type ChecklistStatus = keyof typeof CHECKLIST_STATUS_LABELS;

/** Kiểm kết quả checklist: đủ mục, mục Không đạt có ảnh + ghi chú; trả trạng thái và lỗi. */
export function evaluateChecklist(items: ChecklistItemDef[], results: ChecklistResultInput[]): { status: Exclude<ChecklistStatus, 'unlocked'>; errors: { path: string; message: string }[]; failed: ChecklistItemDef[] } {
  const errors: { path: string; message: string }[] = [];
  const byId = new Map(results.map((r) => [r.itemId, r]));
  const failed: ChecklistItemDef[] = [];
  for (const it of items) {
    const r = byId.get(it.id);
    if (!r) {
      errors.push({ path: `item.${it.id}`, message: `Chưa kiểm "${it.label}"` });
      continue;
    }
    if (!r.ok) {
      failed.push(it);
      if (!r.photoId) errors.push({ path: `item.${it.id}.photo`, message: `"${it.label}" Không đạt: chụp ảnh` });
      if (!r.note?.trim()) errors.push({ path: `item.${it.id}.note`, message: `"${it.label}" Không đạt: ghi chú` });
    }
  }
  for (const r of results) if (!items.some((i) => i.id === r.itemId)) errors.push({ path: `item.${r.itemId}`, message: 'Mục không thuộc danh mục của xe' });
  const status = failed.some((f) => f.severe) ? 'locked' : failed.length ? 'minor' : 'passed';
  return { status, errors, failed };
}

/** Phút làm tròn giờ (hiển thị "2,5 giờ"). */
export const hoursLabel = (minutes: number) => String(Math.round((minutes / 60) * 100) / 100).replace('.', ',');

/** DEC-761: phút tính từ 0h ngày nhận xe của mốc giờ (giờ trước 12:00 hiểu là hôm sau) */
const tierMinute = (t: string) => {
  const m = hhmmToMin(t);
  return m < 720 ? m + 1440 : m;
};
/** DEC-761: bật hỗ trợ đêm theo mức khi có ít nhất 1 đơn giá tài > 0 */
export const nightTiersOn = (r: DriverRuleParams) => r.nightTier1Rate > 0 || r.nightTier2Rate > 0 || r.nightTier3Rate > 0;
/**
 * DEC-761: tiền hỗ trợ đêm theo mức của giờ xe về bãi `end` (phút từ 0h ngày nhận xe, đã cắt ở giờ về tối đa).
 * Lấy mức cao nhất mà giờ về đạt tới; null = chưa tới mức 1.
 */
export function nightTierPay(end: number, r: DriverRuleParams, role: 'primary' | 'assistant'): { tier: 1 | 2 | 3; amount: number } | null {
  const tiers = [
    { tier: 3 as const, from: tierMinute(r.nightTier3From), rate: role === 'primary' ? r.nightTier3Rate : r.nightTier3AssistantRate },
    { tier: 2 as const, from: tierMinute(r.nightTier2From), rate: role === 'primary' ? r.nightTier2Rate : r.nightTier2AssistantRate },
    { tier: 1 as const, from: tierMinute(r.nightTier1From), rate: role === 'primary' ? r.nightTier1Rate : r.nightTier1AssistantRate },
  ];
  const hit = tiers.find((t) => end >= t.from);
  return hit ? { tier: hit.tier, amount: hit.rate } : null;
}
